/**
 * The dashboard's login, kept alive.
 *
 * An access token lives 30 minutes. Behind it the backend keeps a session whose
 * secret sits in an httpOnly cookie (invisible to scripts); `/auth/refresh`
 * trades that cookie for the next access token. Every authenticated request
 * goes through `authFetch`, which renews the token shortly before it runs out
 * and once more if the server refuses it.
 *
 * The one rule: only the server saying "this session is over" (401 on refresh)
 * signs the user out. A dropped connection, a restarting backend or a failed
 * renewal never do.
 */
import { SESSION_API_BASE_URL } from '../config/api';

/** Fired once when the session is over for good; AuthContext sends the user to /login. */
export const AUTH_ERROR_EVENT = 'auth-error';

const TOKEN_KEY = 'token';
const EXPIRES_AT_KEY = 'token_expires_at';
const LOGOUT_PENDING_KEY = 'logout_pending';
/**
 * Set by the server next to the httpOnly session cookie, readable here and free
 * of secrets: "this browser holds a session". A marker of our own in
 * localStorage would not do — Safari wipes script-written storage after a week
 * away, and the session it pointed to would never be asked for again.
 */
const SESSION_MARKER_COOKIE = 'tg_signed_in';

/** Renew this long before the token runs out, so no request leaves with a dying token. */
const REFRESH_AHEAD_MS = 60_000;
/** Waits before asking again after a failed renewal (deploy restart, flaky connection). */
const RETRY_DELAYS_MS = [500, 1500];
/** A renewal that hangs would hold up every request waiting for its token. */
const RENEWAL_TIMEOUT_MS = 10_000;
const LOGOUT_TIMEOUT_MS = 5_000;

/** Marks a call as coming from the dashboard's own script; the backend ties sessions to it. */
export const SESSION_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' };

const SESSION_REQUEST: RequestInit = {
  method: 'POST',
  credentials: 'include',
  headers: SESSION_HEADERS,
};

/** The token needs renewing but the server could not be reached. The login itself is untouched. */
export class SessionUnavailableError extends Error {
  constructor(message = 'Die Sitzung konnte gerade nicht verlängert werden.') {
    super(message);
    this.name = 'SessionUnavailableError';
  }
}

interface TokenResponse {
  access_token?: string;
  expires_in?: number | null;
}

type RenewalAnswer = { token: string; expiresIn?: number | null } | 'over' | 'unreachable';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function readToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function hasSessionCookie(): boolean {
  return document.cookie.split(';').some((part) => part.trim() === `${SESSION_MARKER_COOKIE}=1`);
}

/** `exp` claim in ms, 0 when unreadable. Only a fallback for tokens stored without a lifetime. */
function jwtExpiryMs(token: string): number {
  try {
    const payload = token.split('.')[1] ?? '';
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/**
 * When the stored token runs out, on this device's clock. Stamped at receipt
 * from the server's `expires_in`: comparing `exp` against a wrong device clock
 * would renew on every request or never.
 */
function expiryMs(token: string): number {
  const stored = Number(localStorage.getItem(EXPIRES_AT_KEY));
  return Number.isFinite(stored) && stored > 0 ? stored : jwtExpiryMs(token);
}

/**
 * Usable without asking for a new one first. A token whose expiry cannot be
 * told (no stamp, no readable `exp`) counts: the server decides, and its 401
 * leads to the renewal anyway.
 */
const isFresh = (token: string) => {
  const expiry = expiryMs(token);
  return expiry === 0 || expiry - Date.now() > REFRESH_AHEAD_MS;
};

/** Store what a login or a renewal returned. */
export function storeSession(token: string, expiresInSeconds?: number | null): void {
  localStorage.setItem(TOKEN_KEY, token);
  if (typeof expiresInSeconds === 'number' && expiresInSeconds > 0) {
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresInSeconds * 1000));
  } else {
    localStorage.removeItem(EXPIRES_AT_KEY);
  }
  // A logout that never reached the server must not end this new login.
  localStorage.removeItem(LOGOUT_PENDING_KEY);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  document.cookie = `${SESSION_MARKER_COOKIE}=; Max-Age=0; Path=/`;
}

/** Whether this browser may be signed in (no request made). False for first-time visitors. */
export function hasSessionHint(): boolean {
  return readToken() !== null || hasSessionCookie();
}

async function askForToken(): Promise<RenewalAnswer> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), RENEWAL_TIMEOUT_MS);
  try {
    const response = await fetch(`${SESSION_API_BASE_URL}/refresh`, { ...SESSION_REQUEST, signal: abort.signal });
    if (response.status === 401) return 'over';
    if (!response.ok) return 'unreachable';
    const data = (await response.json()) as TokenResponse;
    return data.access_token ? { token: data.access_token, expiresIn: data.expires_in } : 'unreachable';
  } catch (error) {
    // No connection, no answer in time, or an answer that is not ours (no JSON).
    console.warn('Session renewal did not reach the server:', error);
    return 'unreachable';
  } finally {
    clearTimeout(timer);
  }
}

let renewal: Promise<string | null> | null = null;

async function requestRenewal(): Promise<string | null> {
  const tokenAtStart = readToken();

  // The login changed while we were busy — a logout or a new login, here or in
  // another tab. Theirs stands: an answer to the old login must neither bring
  // it back nor overwrite the new one.
  const takenOver = (): { token: string | null } | null => {
    const current = readToken();
    if (current === tokenAtStart) return null;
    // After a logout, also drop the marker a late answer may have set again.
    if (current === null) clearSession();
    return { token: current };
  };

  for (let attempt = 0; ; attempt += 1) {
    const answer = await askForToken();
    const afterAnswer = takenOver();
    if (afterAnswer) return afterAnswer.token;

    if (answer === 'over') {
      clearSession();
      return null;
    }
    if (answer !== 'unreachable') {
      storeSession(answer.token, answer.expiresIn);
      return answer.token;
    }
    if (attempt >= RETRY_DELAYS_MS.length) throw new SessionUnavailableError();
    await sleep(RETRY_DELAYS_MS[attempt]);
    const afterWait = takenOver();
    if (afterWait) return afterWait.token;
  }
}

/**
 * A new access token, or null when the session is over. `rejected` is the
 * token that proved unusable; if a newer one is already stored (another
 * request or another tab renewed it), that one is returned without asking.
 */
function renewAccessToken(rejected: string | null): Promise<string | null> {
  const current = readToken();
  if (current && current !== rejected && isFresh(current)) return Promise.resolve(current);
  if (!renewal) {
    renewal = requestRenewal().finally(() => {
      renewal = null;
    });
  }
  return renewal;
}

/**
 * An access token good for at least the next minute, or null when nobody is
 * signed in. Throws SessionUnavailableError when the token has run out and the
 * server cannot be reached to renew it.
 */
export async function getAccessToken(): Promise<string | null> {
  const token = readToken();
  if (token && isFresh(token)) return token;
  if (!hasSessionHint()) return null;
  try {
    return await renewAccessToken(token);
  } catch (error) {
    // Not renewed, but the old token still has seconds left: use them.
    if (token && readToken() === token && expiryMs(token) > Date.now()) return token;
    throw error;
  }
}

function toHeaderRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...(headers as Record<string, string>) };
}

function withBearer(init: RequestInit, token: string | null): RequestInit {
  const headers = toHeaderRecord(init.headers);
  if (token) headers.Authorization = `Bearer ${token}`;
  return { ...init, headers };
}

function signalSessionEnded(): void {
  clearSession();
  window.dispatchEvent(new Event(AUTH_ERROR_EVENT));
}

/**
 * `fetch` for the backend's protected routes: sends the access token, renews
 * it when needed, and repeats the request once if the server refused it. A
 * 401 that comes back from here is final — the session is over and
 * AUTH_ERROR_EVENT has been fired.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const response = await fetch(input, withBearer(init, token));
  if (response.status !== 401) return response;

  // Without a token nothing can be renewed: getAccessToken already tried.
  const renewed = token ? await renewAccessToken(token) : null;
  if (!renewed) {
    signalSessionEnded();
    return response;
  }
  const retried = await fetch(input, withBearer(init, renewed));
  if (retried.status === 401) signalSessionEnded();
  return retried;
}

/** Log out on this device: forget the login here and end the session on the server. */
export async function endSession(): Promise<void> {
  clearSession();
  localStorage.setItem(LOGOUT_PENDING_KEY, '1');
  await finishPendingLogout();
}

/**
 * Repeat a logout that did not reach the server (called on every start).
 * Without it the session cookie would outlive the click on "Abmelden".
 */
export async function finishPendingLogout(): Promise<void> {
  if (localStorage.getItem(LOGOUT_PENDING_KEY) !== '1') return;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), LOGOUT_TIMEOUT_MS);
  try {
    const response = await fetch(`${SESSION_API_BASE_URL}/logout`, {
      ...SESSION_REQUEST,
      signal: abort.signal,
      keepalive: true,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch (error) {
    console.warn('Logout did not reach the server; it is repeated on the next start.', error);
  } finally {
    clearTimeout(timer);
  }
}
